<template>
  <div ref="tabs" class="catalog-tabs">
    <button v-for="tab in tabs"
            :key="tab.key"
            :class="['catalog-tabs__btn', { 'catalog-tabs__btn--active': value === tab.key }]"
            @click="selectTab(tab.key)"
    >
      <component v-if="tab.icon" :is="tab.icon" :size="15" class="catalog-tabs__icon"/>
      <span class="catalog-tabs__label">{{ tab.label }}</span>
      <span v-if="tab.badge" class="catalog-tabs__badge"></span>
    </button>
  </div>
</template>

<script>
export default {
  name: "AzconTabBar",
  props: {
    tabs: {
      type: Array,
      required: true,
    },
    value: {
      type: String,
      required: true,
    },
  },
  watch: {
    value() {
      this.$nextTick(this.centerActiveTab);
    },
  },
  mounted() {
    this.$nextTick(this.centerActiveTab);
  },
  methods: {
    selectTab(tabKey) {
      this.$emit('input', tabKey);
      this.$nextTick(this.centerActiveTab);
    },

    centerActiveTab() {
      const tabs = this.$refs.tabs;
      if (!tabs || typeof window === 'undefined' || window.innerWidth > 759) return;

      const activeTab = tabs.querySelector('.catalog-tabs__btn--active');
      if (!activeTab) return;

      const targetScrollLeft = activeTab.offsetLeft - ((tabs.clientWidth - activeTab.offsetWidth) / 2);
      const maxScrollLeft = tabs.scrollWidth - tabs.clientWidth;
      const scrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScrollLeft));

      if (typeof tabs.scrollTo === 'function') {
        tabs.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      } else {
        tabs.scrollLeft = scrollLeft;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.catalog-tabs {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  border-bottom: 1.5px solid rgba(0, 0, 0, 0.06);
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  overscroll-behavior-y: none;
  touch-action: pan-x;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0 0 10px;
    border: none;
    outline: none;
    background: transparent;
    color: var(--muted, #5f6f82);
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    position: relative;
    min-height: 44px;
    flex: 0 0 auto;
    -webkit-tap-highlight-color: transparent;
    transition: color 0.3s ease;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: -1.5px;
      height: 2px;
      background: var(--primary, #2f6fe4);
      border-radius: 1px;
      transform: scaleX(0);
      transition: transform 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
    }

    &:hover {
      color: var(--text, #1f2a37);
    }

    &--active {
      color: var(--primary, #2f6fe4);

      &::after {
        transform: scaleX(1);
      }
    }
  }

  &__badge {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #d4d4d4;
    flex-shrink: 0;
    margin-left: -2px;
    transition: background 0.3s ease;

    .catalog-tabs__btn--active & {
      background: #ef4444;
    }
  }

  &__icon {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    opacity: 0.5;
    transition: opacity 0.3s ease;

    .catalog-tabs__btn--active & {
      opacity: 1;
    }
  }
}
</style>
